export interface UsageGaugeReading {
  /** Panel label as rendered, e.g. "Current session", "Current week (Fable)". */
  label: string;
  /** 0–1 utilization fraction. */
  utilization: number;
}

export interface UsageGauges {
  /** 0–1 utilization for the current 5-hour session window, or null if absent. */
  sessionUsage: number | null;
  /** 0–1 utilization; max across all weekly gauges (all-models + per-model), or null. */
  weeklyUsage: number | null;
  /** Every gauge the panel rendered, labels preserved (session + each weekly row). */
  gauges: UsageGaugeReading[];
}

// ANSI CSI (colors / cursor moves) + OSC sequences left in the raw TUI capture.
// Built via RegExp from the ESC codepoint so no literal control char is embedded
// in the source (which would trip no-control-regex / confuse editors).
const ESC = "\\u001b";
const ANSI_CSI = new RegExp(`${ESC}\\[[0-9;?]*[ -/]*[@-~]`, "g");
const ANSI_OSC = new RegExp(`${ESC}\\][^\\u0007${ESC}]*(?:\\u0007|${ESC}\\\\)`, "g");
// Box-drawing + block/bar glyphs (U+2500–U+259F) the panel draws its gauges with.
const BAR_GLYPHS = /[─-▟]/g;

/**
 * Normalize `/usage` output into plain, gauge-parseable text.
 *
 * The interactive `/usage` panel — the only place the CLI renders the session /
 * weekly gauges (`claude -p /usage` omits them entirely as of CLI 2.x) — is
 * drawn with ANSI escapes and Unicode progress-bar glyphs that glue directly
 * onto the percentage (e.g. `Current session███…30%used`). Stripping them
 * leaves the label and number separated by plain whitespace, which also matches
 * the older `claude -p "/usage"` plain-text layout.
 */
function normalizeUsageText(stdout: string): string {
  return stdout
    .replace(ANSI_CSI, "")
    .replace(ANSI_OSC, "")
    .replace(BAR_GLYPHS, " ")
    .replace(/\r/g, "");
}

/**
 * Parse the text of `/usage` into the two gauge numbers we store. Best-effort:
 * any gauge that isn't present becomes null so the poller can skip reporting
 * (e.g. API-key pods have no subscription gauges).
 *
 * Handles both layouts:
 *   - legacy `claude -p "/usage"`: `Current session: 30% used`
 *   - current interactive panel:   `Current session … 30%used` (no colon,
 *     percentage optionally glued to `used`).
 */
export function parseUsageGauges(stdout: string): UsageGauges {
  const text = normalizeUsageText(stdout);
  // One pattern for every gauge row: the kind (session/week), an optional
  // parenthetical model qualifier ("(all models)", "(Fable)"), then the
  // percentage. The qualifier must be captured BEFORE the lazy filler so
  // per-model weekly rows keep their identity instead of collapsing.
  const rows = [
    ...text.matchAll(
      /Current (session|week)\s*(\([^)\n]*\))?[^%\n]*?(\d+(?:\.\d+)?)\s*%(?:\s*used)?/gi,
    ),
  ];

  const gauges: UsageGaugeReading[] = rows.map((m) => ({
    label: `Current ${m[1].toLowerCase()}${m[2] ? ` ${m[2]}` : ""}`,
    utilization: Number(m[3]) / 100,
  }));

  const session = gauges.find((g) => g.label.startsWith("Current session"));
  const weekly = gauges.filter((g) => g.label.startsWith("Current week"));
  return {
    sessionUsage: session ? session.utilization : null,
    weeklyUsage: weekly.length ? Math.max(...weekly.map((g) => g.utilization)) : null,
    gauges,
  };
}
